import { API_ADDRESS, PUBLIC_API_ADDRESS, RUN_ADDRESS, PUBLIC_RUN_ADDRESS } from '@/constants/env';

/**
 * API Configuration Service
 * Manages authentication state and provides appropriate API addresses
 * based on whether the user is authenticated or in public mode.
 */

let isAuthenticated = false;

/**
 * Checks if the user is authenticated by querying the user organizations endpoint.
 * Returns true if authenticated (response is OK and contains user organizations array), false otherwise.
 * Defaults to false (public mode) if the check fails.
 */
export async function checkAuthentication(): Promise<boolean> {
  // First we just refresh the aip session
  try {
    await fetch(
      `${API_ADDRESS}/decorator-permissions/v1/licenses/me?gcp-iap-mode=SESSION_REFRESHER`,
      {
        credentials: 'include',
        method: 'GET',
      },
    );
  } catch (error) {
    console.error('Error refreshing api session:', error);
  }

  try {
    const response = await fetch(`${API_ADDRESS}/decorator-permissions/v1/licenses/me`, {
      credentials: 'include',
      method: 'GET',
    });

    // Check if response is OK (status 200-299)
    if (!response.ok) {
      return false;
    }

    const contentType = response.headers.get('content-type');

    // Check if response is JSON (authenticated user)
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      // Verify it's an array (user organizations)
      if (Array.isArray(data)) {
        return true;
      }
    }

    // If response doesn't have expected structure, user is not authenticated
    return false;
  } catch (error) {
    console.warn('Authentication check failed, defaulting to public mode:', error);
    return false;
  }
}

/**
 * Sets the authentication status.
 * Should be called during app initialization after checking auth.
 */
export function setAuthenticationStatus(authenticated: boolean): void {
  isAuthenticated = authenticated;
}

/**
 * Returns the appropriate API address based on authentication status.
 */
export function getApiAddress(): string {
  return isAuthenticated ? API_ADDRESS : PUBLIC_API_ADDRESS;
}

/**
 * Returns the appropriate RUN address based on authentication status.
 */
export function getRunAddress(): string {
  return isAuthenticated ? RUN_ADDRESS : PUBLIC_RUN_ADDRESS;
}

/**
 * Returns whether the user is currently authenticated.
 */
export function isUserAuthenticated(): boolean {
  return isAuthenticated;
}
